import { Page } from "@ludens-reklame/rubics-v3-react/page";
import { Wrapper } from "./internal-components/Wrapper.js";
import { ThemeStore } from "./types/store.js";
import { Image } from "./components/Image/Image.js";
import { Hero } from "./components/Hero/Hero.js";
import { TextContent } from "./components/TextContent/TextContent.js";
import { Showcases } from "./components/Showcases/Showcases.js";
import { Video } from "./components/Video/Video.js";
import { InfoGraphic } from "./components/InfoGraphic/InfoGraphic.js";
import { MyPage } from "./components/MyPage/MyPage.js";
import { Product } from "./components/Product/Product.js";
import { Cart } from "./components/Cart/Cart.js";
import { FilterableProducts } from "./components/FilterableProducts/FilterableProducts.js";
import { SelectedProducts } from "./components/SelectedProducts/SelectedProducts.js";
import { Header } from "./components/Header/Header.js";
import { Embed } from "./components/Embed/Embed.js";
import { FactBox } from "./components/FactBox/FactBox.js";
import { CTA } from "./components/CTA/CTA.js";
import { Quote } from "./components/Quote/Quote.js";
import { LogoSlider } from "./components/LogoSlider/LogoSlider.js";
import { TabNav } from "./components/TabNav/TabNav.js";
import { Employees } from "./components/Employees/Employees.js";
import { ShortVersion } from "./components/ShortVersion/ShortVersion.js";
import { RelatedArticles } from "./components/RelatedArticles/RelatedArticles.js";
import { Reviews } from "./components/Reviews/Reviews.js";
import { ServiceGrid } from "./components/ServiceGrid/ServiceGrid.js";
import { ServiceGridV2 } from "./components/ServiceGridV2/ServiceGridV2.js";
import { Footer } from "./components/Footer/Footer.js";
import { ImageGrid } from "./components/ImageGrid/ImageGrid.js";
import { FactIllustration } from "./components/FactIllustration/FactIllustration.js";
import { Breadcrumbs } from "./components/Breadcrumbs/Breadcrumbs.js";
import { Banner } from "./components/Banner/Banner.js";
import { BannerAnimation } from "./components/BannerAnimation/BannerAnimation.js";
import { ContactForm } from "./components/ContactForm/ContactForm.js";
import { Checkout } from "./components/Checkout/Checkout.js";
import { ProductComparison } from "./components/ProductComparison/ProductComparison.js";
import { CardGrid } from "./components/CardGrid/CardGrid.js";
import { SearchResults } from "./components/SearchResults/SearchResults.js";
import { VideoBanner } from "./components/VideoBanner/VideoBanner.js";
import { VideoComponent } from "./components/VideoComponent/VideoComponent.js";
import { ExpandableContent } from "./components/ExpandableContent/ExpandableContent.js";
import { DownloadableFiles } from "./components/DownloadableFiles/DownloadableFiles.js";
import { Pages } from "./components/Pages/Pages.js";
import { InteractiveMap } from "./components/InteractiveMap/InteractiveMap.js";
import { Navigation } from "./components/Header/Navigation.js";
import { ImageShowcase } from "./components/ImageShowcase/ImageShowcase.js";
import { CoverPhoto } from "./components/CoverPhoto/CoverPhoto.js";
import { Karusell } from "./components/Karusell/Karusell.js";
import { JobListings } from "./components/JobListings/JobListings.js";
import { InfoKort } from "./components/InfoKort/InfoKort.js";
import { HeroLogo } from "./components/HeroLogo/HeroLogo.js";
import { Markets } from "./components/Markets/Markets.js";

interface Props {
  store: ThemeStore;
}

function App({ store }: Props) {
  return (
    <Page
      store={store}
      useCartContext
      renderComponent={(s, children) => {
        const Component = getComponent(s.component);
        return (
          // @ts-ignore Can't be bothered to type this now
          <Component key={s.name} component={s.component} {...s.props}>
            {children}
          </Component>
        );
      }}
    >
      {({ header, main, footer }) => {
        const headerIndex = header.findIndex((component) =>
          component?.key?.includes("header")
        );
        const headerExists = headerIndex > -1;

        return (
          <Wrapper>
            {header}
            {headerExists && (
              <Navigation
                themeSettings={store.themeSettings}
              />
            )}
            <main>{main}</main>
            {footer}
          </Wrapper>
        );
      }}
    </Page>
  );
}

function getComponent(
  name?: string
): React.FunctionComponent | typeof FilterableProducts {
  switch (name) {
    case "VideoComponent":
      return VideoComponent;
    case "VideoBanner":
      return VideoBanner;
    case "Image":
      return Image;
    case "Hero":
      return Hero;
    case "TextContent":
      return TextContent;
    case "Showcases":
      return Showcases;
    case "Video":
      return Video;
    case "InfoGraphic":
      return InfoGraphic;
    case "MyPage":
      return MyPage;
    case "Product":
      return Product;
    case "Cart":
      return Cart;
    case "FilterableProducts":
    case "RelatedProducts":
      return FilterableProducts;
    case "SelectedProducts":
      return SelectedProducts;
    case "Header":
      return Header;
    case "Embed":
      return Embed;
    case "FactBox":
      return FactBox;
    case "CTA":
      return CTA;
    case "Quote":
      return Quote;
    case "LogoSlider":
      return LogoSlider;
    case "TabNav":
      return TabNav;
    case "Employees":
      return Employees;
    case "ShortVersion":
      return ShortVersion;
    case "RelatedArticles":
      return RelatedArticles;
    case "Reviews":
      return Reviews;
    case "ServiceGrid":
      return ServiceGrid;
    case "ServiceGridV2":
      return ServiceGridV2;
    case "Footer":
      return Footer;
    case "ImageGrid":
      return ImageGrid;
    case "FactIllustration":
      return FactIllustration;
    case "Breadcrumbs":
      return Breadcrumbs;
    case "Banner":
      return Banner;
    case "BannerAnimation":
      return BannerAnimation;
    case "ContactForm":
      return ContactForm;
    case "Checkout":
      return Checkout;
    case "ProductComparison":
      return ProductComparison;
    case "CardGrid":
      return CardGrid;
    case "SearchResults":
      return SearchResults;
    case "ExpandableContent":
      return ExpandableContent;
    case "DownloadableFiles":
      return DownloadableFiles;
    case "Pages":
      return Pages;
    case "InteractiveMap":
      return InteractiveMap;
    case "ImageShowcase":
      return ImageShowcase;
    case "CoverPhoto":
      return CoverPhoto;
    case "Karusell":
      return Karusell;
    case "JobListings":
      return JobListings;
    case "JobInfo":
      return InfoKort;
    case "HeroLogo":
      return HeroLogo;
    case "Markets":
      return Markets;
    default:
      return () => null;
  }
}

export default App;
